import style from './SummaryPage.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import useTranslation from '../../localization/i18n.context';
import AppBar from '../../components/shared/AppBar/AppBar';
import NavigationUtils from '../../utils/navigation.utils';
import CommissionService from '../../services/commission.service';
import { renderIf } from '../../utils/render.utils';
import LoadingScreen from '../../components/shared/LoadingScreen/LoadingScreen';
import SummaryPageView from '../../components/PeerToPeer/SummaryPageView/SummaryPageView';
import Button from '../../components/shared/Button/Button';
import uuid from '../../utils/uuid.utils';
import TransferService from '../../services/transfer.service';
import P2PResultPage from '../P2PResultPage/P2PResultPage';
import SummaryParser, { SummaryParams } from './SummaryParser';
import { EnterAmountType } from '../EnterAmountPage/EnterAmountProvider';
import Base64 from '../../utils/base64.utils';
import { CompleteStyles } from '../CompletePage/CompletePage';
import { CompletePageSuccessType } from '../CompletePage/CompletePageSuccessConfig';
import BottomSheet from '../../components/shared/BottomSheet/BottomSheet';
import List from '../../components/shared/List/List';
import ListItem from '../../components/shared/List/ListItem';
import Alert from '../../components/shared/Alert/Alert';
import { SummaryBottomSheetProvider } from './SummaryBottomSheetProvider';
import CreditService from '../../services/credit.service';
import { CompletePageFailureType } from '../CompletePage/CompletePageFailureConfig';
import InstallmentsService from '../../services/installments.service';
import { AuthenticateEvent } from '../../sdk/authenticate.event';
import useEventListener from '../../hooks/event.listener.hook';
import { TransferReasons } from '../../components/PeerToPeer/EnterAmountPageView/TransferReasons';

export default function SummaryPage() {
  const { t } = useTranslation();
  const backRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cardTo, setCardTo] = useState();
  const [cardFrom, setCardFrom] = useState();
  const [commission, setCommission] = useState();
  const [total, setTotal] = useState();
  const [response, setResponse] = useState({});
  const [params, setParams] = useState({});
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [selectedOptionBottomSheet, setSelectedOptionBottomSheet] =
    useState(false);
  const paramsRef = useRef(params);

  const currentParams = new URLSearchParams(window.location.search);
  const type = currentParams.get(SummaryParams.TYPE);
  const currency = currentParams.get(SummaryParams.CURRENCY);
  const amount = currentParams.get(SummaryParams.AMOUNT);
  const organizationCode = currentParams.get(SummaryParams.ORGANIZATION_CODE);
  const reasonDescription = currentParams.get(SummaryParams.REASON_DESCRIPTION);
  const dataBottomSheet = SummaryBottomSheetProvider(t, type?.toUpperCase());

  const idempotencyKey = {
    [EnterAmountType.P2P]: `webview-p2p-${uuid()}`,
    [EnterAmountType.MICROLOAN]: `webview-microloan-${uuid()}`,
    [EnterAmountType.MICROLOAN_PAYMENT]: `webview-repayment-${uuid()}`,
    [EnterAmountType.POS]: `webview-pos-${uuid()}`,
    [EnterAmountType.SEND_MONEY_TO_COMPANY]: `webview-send-money-company-${uuid()}`,
  };
  const Title = {
    [EnterAmountType.P2P]: t('Send money'),
    [EnterAmountType.MICROLOAN]: t('Use credit'),
    [EnterAmountType.MICROLOAN_PAYMENT]: t('Pay installments'),
    [EnterAmountType.POS]: t('Top up POS'),
    [EnterAmountType.SEND_MONEY_TO_COMPANY]: t('Send money to Company'),
  };

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const parsedParams = SummaryParser.parseURLParams(urlParams);

    setParams(parsedParams);

    switch (type?.toUpperCase()) {
      case EnterAmountType.P2P: {
        CommissionService.calculate(parsedParams)
          .then(response => {
            setCardTo(response?.data.cardTo);
            setCommission(response?.data.commission);
            setTotal(response?.data.total);
            setCardFrom(response?.data.cardFrom);
            setResponse(response?.data);
            setIsLoading(false);
          })
          .catch(e => {
            console.error(e);
            NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
          });
        break;
      }
      case EnterAmountType.MICROLOAN_PAYMENT: {
        CommissionService.calculate(parsedParams)
          .then(response => {
            setResponse(response?.data);
            setIsLoading(false);
          })
          .catch(e => {
            console.error(e);
            NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
          });
        break;
      }
      case EnterAmountType.MICROLOAN: {
        InstallmentsService.getInstallmentById(
          parsedParams.id,
          parsedParams.organizationCode,
          parsedParams.amount,
        )
          .then(response => {
            setResponse(response?.data);
            setIsLoading(false);
          })
          .catch(e => {
            console.error(e);
            NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
          });
        break;
      }
      case EnterAmountType.POS: {
        CommissionService.calculate(parsedParams)
          .then(response => {
            setResponse(response?.data);
            setIsLoading(false);
          })
          .catch(e => {
            console.error(e);
            NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
          });
        break;
      }
      case EnterAmountType.SEND_MONEY_TO_COMPANY: {
        CommissionService.calculate(parsedParams)
          .then(response => {
            setResponse(response?.data);
            setIsLoading(false);
          })
          .catch(e => {
            console.error(e);
            NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
          });
        break;
      }
    }
  }, []);

  const onAuthenticateResponse = data => {
    const { status } = data;

    if (status === AuthenticateEvent.Status.VERIFIED) {
      switch (type?.toUpperCase()) {
        case EnterAmountType.P2P:
          sendMoney();
          break;
        case EnterAmountType.MICROLOAN: {
          setShowBottomSheet(true);
          setSelectedOptionBottomSheet(false);
          break;
        }
        case EnterAmountType.MICROLOAN_PAYMENT: {
          payCredit();
          break;
        }
        case EnterAmountType.POS: {
          accreditMoney();
          break;
        }
        case EnterAmountType.SEND_MONEY_TO_COMPANY: {
          sendMoneyToCompany();
          break;
        }
      }
    }
  };

  useEventListener(
    onAuthenticateResponse,
    window.__CAAS__.events.authenticatePasscode,
  );

  function onContinueButtonClick() {
    AuthenticateEvent.authenticate();
  }

  function sendMoney() {
    setIsLoading(true);

    const transferReasonCode = (() => {
      const englishReason = TransferReasons.find(reason =>
        reason.DESC_ENG === reasonDescription);
      if (englishReason) return englishReason.CODE;

      const arabicReason = TransferReasons.find(reason =>
        reason.DESC_ARB === reasonDescription);
      if (arabicReason) return arabicReason.CODE;

      return undefined;
    })();

    TransferService.sendMoney(
      idempotencyKey[type?.toUpperCase()],
      paramsRef.current.cardUidFrom,
      paramsRef.current.cardUidTo,
      currency,
      amount,
      transferReasonCode,
    )
      .then(response =>
        NavigationUtils.navigate(
          NavigationUtils.Routes.P2P_RESULT.replace(
            ':type',
            P2PResultPage.Type.SUCCESS,
          ),
          true,
          {
            currency,
            amount,
            cardToName: cardTo.name,
            transactionId: response.data.id,
            createdAt: response.data.createdAt,
          },
        ),
      )
      .catch(() =>
        NavigationUtils.navigate(
          NavigationUtils.Routes.P2P_RESULT.replace(
            ':type',
            P2PResultPage.Type.ERROR,
          ),
        ),
      );
  }

  function payCredit() {
    setIsLoading(true);

    CreditService.payCredit(
      idempotencyKey[type?.toUpperCase()],
      response?.amount,
      response?.beneficiary.organizationCode,
    )
      .then(response => {
        const params = {
          amount: amount,
          transactionId: response.data.id,
          createdAt: response.data.date ?? new Date(),
          organizationCode: organizationCode,
        };
        const encodedParams = Base64.encode(JSON.stringify(params));
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.SUCCESS}/${CompletePageSuccessType.CREDIT_REPAY_SUCCESS}`,
          false,
          { params: encodedParams },
        );
      })
      .catch(() =>
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.FAILURE}/${CompletePageFailureType.CREDIT_REPAY_FAILURE}`,
          false,
        ),
      );
  }

  function accreditMoney() {
    setIsLoading(true);
    TransferService.accreditMoney(
      idempotencyKey[type?.toUpperCase()],
      paramsRef.current.amount,
      paramsRef.current.merchantId,
      paramsRef.current.organizationCode,
    )
      .then(response => {
        const params = {
          amount: response?.data?.amount,
          transactionId: response?.data?.transactionId,
          createdAt: response?.data?.date ?? new Date(),
          organizationCode: organizationCode,
        };
        const encodedParams = Base64.encode(JSON.stringify(params));
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.SUCCESS}/${CompletePageSuccessType.POS_SUCCESS}`,
          false,
          { params: encodedParams },
        );
      })
      .catch(() =>
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.FAILURE}/${CompletePageFailureType.POS_FAILURE}`,
          false,
        ),
      );
  }

  function sendMoneyToCompany() {
    setIsLoading(true);
    TransferService.sendMoneyToCompany(
      idempotencyKey[type?.toUpperCase()],
      paramsRef.current.organizationCode,
      paramsRef.current.currency,
      paramsRef.current.amount,
    )
      .then(response => {
        const params = {
          amount: paramsRef.current.amount,
          transactionId: response?.data?.transactionId,
          createdAt: response?.data?.date ?? new Date(),
          organizationCode: organizationCode,
        };
        const encodedParams = Base64.encode(JSON.stringify(params));
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.SUCCESS}/${CompletePageSuccessType.SEND_MONEY_TO_COMPANY_SUCCESS}`,
          false,
          { params: encodedParams },
        );
      })
      .catch(() =>
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.FAILURE}/${CompletePageFailureType.SEND_MONEY_TO_COMPANY_FAILURE}`,
          false,
        ),
      );
  }

  function onConfirmBottomSheetButtonClick() {
    setShowBottomSheet(false);
    setIsLoading(true);
    CreditService.takeCredit(
      idempotencyKey[type?.toUpperCase()],
      response?.amount,
      response?.beneficiary.organizationCode,
      response?.installments,
      EnterAmountType.MICROLOAN,
    )
      .then(response => {
        const params = { amount: amount, organizationCode: organizationCode };
        const encodedParams = Base64.encode(JSON.stringify(params));
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.SUCCESS}/${CompletePageSuccessType.CREDIT_MICROLOAN_SUCCESS}`,
          false,
          { params: encodedParams },
        );
      })
      .catch(() =>
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.COMPLETE}/${CompleteStyles.FAILURE}/${CompletePageFailureType.CREDIT_MICROLOAN_FAILURE}`,
          false,
        ),
      );
  }

  return (
    <>
      {renderIf(isLoading, <LoadingScreen />)}
      {renderIf(
        !isLoading,
        <>
          <AppBar
            onBackClick={() => {
              NavigationUtils.back();
            }}
            title={Title[type?.toUpperCase()] ?? ''}
            backRef={backRef}
            className="fixed-bar"
          />
          <SummaryPageView
            params={response}
            currency={currency}
            amount={amount}
            className={style.container}
            cardTo={cardTo}
            cardFrom={cardFrom}
            commission={commission}
            total={total}
            type={type}
            reasonDescription={reasonDescription}
          />
          <div className="fixed-cta">
            <Button fullWidth onClick={onContinueButtonClick}>
              {t('Confirm')}
            </Button>
          </div>
        </>,
      )}
      {renderIf(
        showBottomSheet,
        <BottomSheet
          title={dataBottomSheet?.title}
          onCloseClick={() => {
            setShowBottomSheet(false);
          }}
        >
          <List
            className={style[dataBottomSheet?.titleStyle]}
            data-testid="card-list"
          >
            <ListItem
              title={dataBottomSheet?.itemTitle}
              actionable
              type={ListItem.Type.RADIO}
              onClick={() => {
                setSelectedOptionBottomSheet(true);
              }}
            />
          </List>
          <Alert
            hasIcon
            className={style[dataBottomSheet?.alertStyle]}
            message={dataBottomSheet?.alertMessage}
          />
          <Button
            fullWidth
            onClick={onConfirmBottomSheetButtonClick}
            disabled={!selectedOptionBottomSheet}
          >
            {dataBottomSheet?.buttonTitle}
          </Button>
        </BottomSheet>,
      )}
    </>
  );
}
